import { useEffect, useState } from "react";
import { SuperSEO } from "react-super-seo";
import { Helmet } from 'react-helmet';
import { getCanonicalUrl } from "../utils/domain";

interface FAQItem {
    id: string;
    category: string;
    question: string;
    answer: string | React.ReactNode;
}

function FAQs() {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [openId, setOpenId] = useState<string | null>("gen-1");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const categories = [
        { id: "all", label: "All Questions", icon: "fa-solid fa-list-check" },
        { id: "general", label: "General", icon: "fa-solid fa-circle-question" },
        { id: "self-drive", label: "Self-Drive", icon: "fa-solid fa-car-side" },
        { id: "chauffeur", label: "Chauffeur", icon: "fa-solid fa-user-tie" },
        { id: "transfers", label: "Pick & Drop", icon: "fa-solid fa-location-dot" },
        { id: "pricing", label: "Pricing & Payments", icon: "fa-solid fa-wallet" },
        { id: "cancellations", label: "Cancellations", icon: "fa-solid fa-arrow-rotate-left" },
        { id: "insurance", label: "Insurance & Safety", icon: "fa-solid fa-shield" }
    ];

    const faqData: FAQItem[] = [
        // General
        {
            id: "gen-1",
            category: "general",
            question: "What is Aapli Gaadi?",
            answer: "Aapli Gaadi is a premier car rental platform originating from the Konkan region of Maharashtra. We offer self-drive cars, chauffeur-driven rides, self-drive bikes, and station transfer services across Maharashtra."
        },
        {
            id: "gen-2",
            category: "general",
            question: "Where are your major operational hubs?",
            answer: "We operate across major hubs including Mumbai, Navi Mumbai, Thane, Kudal, Ratnagiri, Kankavli, Vaibhavwadi, Sawantwadi, Vengurla, Malvan, Tarkarli, Devbag, Devgad, and Ganpatipule."
        },
        {
            id: "gen-3",
            category: "general",
            question: "What services do you offer?",
            answer: "Our core services include Self-Drive Car Rentals, Chauffeur-Driven Cars, Self-Drive Bike Rentals, Pick & Drop Transfers, and Corporate/Long-Term Subscriptions."
        },
        {
            id: "gen-4",
            category: "general",
            question: "How can I book a vehicle?",
            answer: "You can easily select your location, pickup/dropoff dates, and vehicle on our website. Clicking 'Book Now via WhatsApp' connects you directly to our reservation team for instant booking confirmation."
        },
        {
            id: "gen-5",
            category: "general",
            question: "Do you offer corporate or long-term rentals?",
            answer: "Yes, we provide customized, cost-effective long-term car subscriptions and corporate fleet solutions. Contact our support team for specialized business packages."
        },

        // Self-Drive
        {
            id: "sd-1",
            category: "self-drive",
            question: "What documents are required for self-drive rentals?",
            answer: (
                <div>
                    <p className="mb-3 text-secondary" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                        To ensure a smooth & hassle-free vehicle pickup, please present the original physical copies of the following mandatory documents during key handover:
                    </p>
                    <div className="row">
                        {/* 1. Driving License Card */}
                        <div className="col-md-4 col-sm-6 mb-3">
                            <div className="p-3 bg-white rounded shadow-sm h-100 border" style={{ borderLeft: '4px solid #01d28e', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center mr-2 text-white" style={{ width: '32px', height: '32px', backgroundColor: '#01d28e', fontSize: '13px' }}>
                                        <i className="fa-solid fa-id-card"></i>
                                    </div>
                                    <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: '14px' }}>Driving License</h6>
                                </div>
                                <ul className="pl-3 mb-0 text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                                    <li>Valid & active Indian DL</li>
                                    <li><strong className="text-dark">Minimum 2 years old</strong></li>
                                    <li>Original physical copy</li>
                                </ul>
                            </div>
                        </div>

                        {/* 2. Identity Proof Card */}
                        <div className="col-md-4 col-sm-6 mb-3">
                            <div className="p-3 bg-white rounded shadow-sm h-100 border" style={{ borderLeft: '4px solid #3b82f6', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center mr-2 text-white" style={{ width: '32px', height: '32px', backgroundColor: '#3b82f6', fontSize: '13px' }}>
                                        <i className="fa-solid fa-address-card"></i>
                                    </div>
                                    <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: '14px' }}>Identity Proof</h6>
                                </div>
                                <ul className="pl-3 mb-0 text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                                    <li>Original <strong className="text-dark">Aadhaar Card</strong> or <strong className="text-dark">Passport</strong></li>
                                    <li>Matching driver's name</li>
                                </ul>
                            </div>
                        </div>

                        {/* 3. Address Proof Card */}
                        <div className="col-md-4 col-sm-6 mb-3">
                            <div className="p-3 bg-white rounded shadow-sm h-100 border" style={{ borderLeft: '4px solid #f59e0b', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center mr-2 text-white" style={{ width: '32px', height: '32px', backgroundColor: '#f59e0b', fontSize: '13px' }}>
                                        <i className="fa-solid fa-house-user"></i>
                                    </div>
                                    <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: '14px' }}>Address Proof</h6>
                                </div>
                                <ul className="pl-3 mb-0 text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                                    <li>Local address proof (Utility bill / Rent agreement)</li>
                                    <li>Verified mobile number</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="mt-1 p-2 px-3 rounded bg-light border text-muted small d-flex align-items-center" style={{ fontSize: '12px' }}>
                        <i className="fa-solid fa-circle-info text-primary mr-2" style={{ fontSize: '14px' }}></i>
                        <span>Digital copies on mParivahan / DigiLocker are accepted for initial verification, but physical verification of original DL at pickup is mandatory.</span>
                    </div>
                </div>
            )
        },
        {
            id: "sd-2",
            category: "self-drive",
            question: "Is there a minimum age requirement for self-drive rentals?",
            answer: "Yes, the driver must be at least 25 years old and possess a valid driving license with at least two years of driving experience."
        },
        {
            id: "sd-3",
            category: "self-drive",
            question: "Are there any geographic restrictions on where I can drive?",
            answer: "You can freely drive anywhere within the state of Maharashtra. If you plan an outstation trip outside Maharashtra, please inform us in advance."
        },
        {
            id: "sd-4",
            category: "self-drive",
            question: "What is the fuel policy for self-drive cars?",
            answer: "Fuel is not included in the rental charges. The vehicle should be returned with the same fuel level as when received at pickup."
        },

        // Chauffeur
        {
            id: "ch-1",
            category: "chauffeur",
            question: "How does the 'Car with Driver' service work?",
            answer: "You can hire a premium car accompanied by a verified, professional driver for outstation road trips, local city travel, or family sightseeing tours."
        },
        {
            id: "ch-2",
            category: "chauffeur",
            question: "Are driver charges included in the rental price?",
            answer: "Driver charges and fixed package rates are transparently detailed during booking inquiry. Night allowance or outstation bata (if applicable) will be explicitly specified."
        },

        // Transfers
        {
            id: "tr-1",
            category: "transfers",
            question: "Do you provide airport & railway station pick & drop transfers?",
            answer: "Yes, we offer dedicated station and airport transfer services connecting Goa/Mopa Airport, Chipi Airport (Sindhudurg), Kudal, Sawantwadi, and Kankavli railway stations to coastal hotels and villas."
        },
        {
            id: "tr-2",
            category: "transfers",
            question: "Can I schedule a pickup in advance?",
            answer: "Yes, you can pre-schedule your transfer timing. Our team monitors flight/train arrival schedules so your driver is waiting upon arrival."
        },

        // Pricing & Payments
        {
            id: "pr-1",
            category: "pricing",
            question: "How are rental charges calculated?",
            answer: "Charges are calculated based on vehicle model, daily mileage package (e.g. 150km, 200km, 300km/day), and booking duration. Discounts automatically apply for multi-day rentals."
        },
        {
            id: "pr-2",
            category: "pricing",
            question: "What payment methods do you accept?",
            answer: "We accept online payments via UPI (Google Pay, PhonePe, Paytm, BHIM) and IMPS / NEFT / RTGS Bank Transfers."
        },
        {
            id: "pr-3",
            category: "pricing",
            question: "Is there a security deposit for self-drive rentals?",
            answer: "Yes, a refundable security deposit is payable at vehicle pickup. The deposit is refunded back to your original source within 24 to 48 hours after vehicle check-in inspection."
        },

        // Cancellations
        {
            id: "cn-1",
            category: "cancellations",
            question: "Can I cancel my booking?",
            answer: "Yes, you can cancel your booking before the trip starts. Cancellations made >72 hrs before trip start receive a 90% refund, while cancellations between 24-72 hrs receive a 75% refund of total rental."
        },
        {
            id: "cn-2",
            category: "cancellations",
            question: "When will I receive my cancellation refund?",
            answer: "Eligible refunds are processed automatically back to your original payment source within 7 to 10 business days."
        },

        // Insurance
        {
            id: "in-1",
            category: "insurance",
            question: "Are the rental cars insured?",
            answer: "Yes, all our vehicles carry comprehensive insurance and commercial permits. Damage liability is governed by our transparent Terms & Conditions policy."
        },
        {
            id: "in-2",
            category: "insurance",
            question: "What should I do in case of an emergency or breakdown?",
            answer: "In case of an accident or breakdown, immediately call our 24/7 Roadside Assistance helpline (+91 720 838 8380). We will arrange emergency roadside help or replacement vehicle support."
        }
    ];

    const checkAnswerMatch = (item: FAQItem, query: string) => {
        if (!query) return true;
        const lowerQuery = query.toLowerCase();
        if (item.question.toLowerCase().includes(lowerQuery)) return true;
        if (typeof item.answer === 'string') {
            return item.answer.toLowerCase().includes(lowerQuery);
        }
        return false;
    };

    const filteredFaqs = faqData.filter((item) => {
        const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
        const matchesSearch = checkAnswerMatch(item, searchQuery);
        return matchesCategory && matchesSearch;
    });

    const handleCategorySelect = (catId: string) => {
        setSelectedCategory(catId);
        const matches = faqData.filter((item) => {
            const matchesCat = catId === "all" || item.category === catId;
            const matchesSearch = checkAnswerMatch(item, searchQuery);
            return matchesCat && matchesSearch;
        });
        if (matches.length > 0) {
            setOpenId(matches[0].id);
        } else {
            setOpenId(null);
        }
    };

    // Automatically expand the first question when category or search changes
    useEffect(() => {
        const matches = faqData.filter((item) => {
            const matchesCat = selectedCategory === "all" || item.category === selectedCategory;
            const matchesSearch = checkAnswerMatch(item, searchQuery);
            return matchesCat && matchesSearch;
        });
        if (matches.length > 0) {
            setOpenId(matches[0].id);
        } else {
            setOpenId(null);
        }
    }, [selectedCategory, searchQuery]);

    const toggleAccordion = (id: string) => {
        setOpenId(openId === id ? null : id);
    };

    const getFaqPlainTextAnswer = (item: FAQItem): string => {
        if (typeof item.answer === 'string') {
            return item.answer;
        }
        return "You need a valid Driving License (held for at least 2 years), an Aadhaar Card or Passport for identity verification, and local address proof during vehicle pickup.";
    };

    return (
        <>
            <Helmet>
                <link rel="canonical" href={getCanonicalUrl('/FAQs')} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqData.map(item => ({
                            "@type": "Question",
                            "name": item.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": getFaqPlainTextAnswer(item)
                            }
                        }))
                    })}
                </script>
            </Helmet>
            <SuperSEO
                title="Frequently Asked Questions - Aapli Gaadi Car Rentals"
                description="Find answers to your questions about Aapli Gaadi’s car rental services, booking process, policies, and more. Get quick answers for a hassle-free experience across Mumbai, Navi Mumbai, Thane, Kudal, and beyond."
                openGraph={{
                    ogTitle: "Frequently Asked Questions - Aapli Gaadi Car Rentals",
                    ogDescription:
                        "Get answers to all your queries about Aapli Gaadi’s self-drive and chauffeur-driven car rental services, booking policies, and more.",
                    ogUrl: getCanonicalUrl('/FAQs'),
                    ogType: "website",
                    ogSiteName: "Aapli Gaadi",
                    ogLocale: "en_US"
                }}
            />

            {/* Hero Banner Header */}
            <section className="hero-wrap hero-wrap-2 js-fullheight" style={{ backgroundImage: `url('image/faq_banner.png')` }} data-stellar-background-ratio="0.5">
                <div className="overlay"></div>
                <div className="container">
                    <div className="row no-gutters slider-text js-fullheight align-items-end justify-content-start">
                        <div className="col-md-9 pb-5">
                            <p className="breadcrumbs">
                                <span className="mr-2"><a href="/">Home <i className="ion-ios-arrow-forward"></i></a></span>
                                <span>FAQs <i className="ion-ios-arrow-forward"></i></span>
                            </p>
                            <h1 className="mb-3 bread playwrite-cu-font">Frequently Asked Questions</h1>
                            <p className="text-white-50 lead mb-0" style={{ fontSize: '16px' }}>
                                Find answers to common questions about bookings, pricing, insurance, and vehicle policies.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content Area */}
            <section className="ftco-section bg-light py-5">
                <div className="container">
                    
                    {/* Live Search Bar */}
                    <div className="row justify-content-center mb-4">
                        <div className="col-lg-8 col-md-10 col-12">
                            <div className="position-relative w-100">
                                <input
                                    type="text"
                                    className="form-control shadow-sm text-dark"
                                    placeholder="Search questions e.g. deposit, license, fuel, cancel..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ 
                                        borderRadius: '50px', 
                                        border: '2px solid #e2e8f0', 
                                        height: '54px', 
                                        paddingLeft: '24px', 
                                        paddingRight: searchQuery ? '45px' : '24px', 
                                        fontSize: '15px',
                                        backgroundColor: '#ffffff'
                                    }}
                                />
                                {searchQuery && (
                                    <button 
                                        type="button"
                                        className="btn btn-link p-0 position-absolute"
                                        onClick={() => setSearchQuery("")}
                                        style={{ 
                                            right: '16px', 
                                            top: '50%', 
                                            transform: 'translateY(-50%)', 
                                            zIndex: 2, 
                                            textDecoration: 'none' 
                                        }}
                                        aria-label="Clear search"
                                    >
                                        <i className="fa-solid fa-circle-xmark" style={{ fontSize: '18px', color: '#94a3b8' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Category Filter Pills */}
                    <div className="d-flex flex-wrap justify-content-center mb-5" style={{ gap: '10px' }}>
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                className={`btn ${selectedCategory === cat.id ? 'btn-primary' : 'btn-white text-dark border'}`}
                                onClick={() => handleCategorySelect(cat.id)}
                                style={{
                                    borderRadius: '25px',
                                    padding: '8px 20px',
                                    fontWeight: selectedCategory === cat.id ? 'bold' : 'normal',
                                    fontSize: '14px',
                                    backgroundColor: selectedCategory === cat.id ? '#0f172a' : '#fff',
                                    borderColor: selectedCategory === cat.id ? '#0f172a' : '#cbd5e1'
                                }}
                            >
                                <i className={`${cat.icon} mr-2`} style={{ color: selectedCategory === cat.id ? '#01d28e' : '#64748b' }}></i>
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {/* FAQ Items Accordion List */}
                    <div className="row justify-content-center mb-5">
                        <div className="col-lg-10">
                            {filteredFaqs.length > 0 ? (
                                <div className="accordion-custom">
                                    {filteredFaqs.map((faq) => {
                                        const isOpen = openId === faq.id;
                                        return (
                                            <div 
                                                key={faq.id} 
                                                className="card border-0 shadow-sm mb-3 rounded-lg overflow-hidden" 
                                                style={{ borderRadius: '12px', border: '1px solid #e2e8f0', transition: 'all 0.2s ease' }}
                                            >
                                                <div
                                                    className="card-header bg-white p-4 cursor-pointer d-flex align-items-center justify-content-between"
                                                    onClick={() => toggleAccordion(faq.id)}
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    <div className="d-flex align-items-center pr-3">
                                                        <div 
                                                            className="d-flex align-items-center justify-content-center rounded-circle mr-3 flex-shrink-0"
                                                            style={{ 
                                                                width: '36px', 
                                                                height: '36px', 
                                                                backgroundColor: isOpen ? 'rgba(1, 210, 142, 0.15)' : '#f8fafc',
                                                                color: isOpen ? '#01d28e' : '#64748b' 
                                                            }}
                                                        >
                                                            <i className="fa-solid fa-circle-question" style={{ fontSize: '18px' }}></i>
                                                        </div>
                                                        <h3 className="h6 font-weight-bold text-dark mb-0" style={{ fontSize: '16px', lineHeight: '1.4' }}>
                                                            {faq.question}
                                                        </h3>
                                                    </div>
                                                    <span 
                                                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                                        style={{ 
                                                            width: '28px', 
                                                            height: '28px', 
                                                            backgroundColor: isOpen ? '#0f172a' : '#f1f5f9', 
                                                            color: isOpen ? '#fff' : '#64748b',
                                                            transition: 'transform 0.2s ease'
                                                        }}
                                                    >
                                                        <i className={`fa-solid ${isOpen ? 'fa-minus' : 'fa-plus'}`} style={{ fontSize: '12px' }}></i>
                                                    </span>
                                                </div>

                                                {isOpen && (
                                                    <div className="card-body p-4 pt-0 bg-white border-top-0">
                                                        <div className="p-3 rounded" style={{ backgroundColor: '#f8fafc', borderLeft: '4px solid #01d28e' }}>
                                                            <div className="mb-0 text-secondary" style={{ fontSize: '15px', lineHeight: '1.7' }}>
                                                                {faq.answer}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="card border-0 shadow-sm rounded-lg p-5 text-center bg-white" style={{ borderRadius: '12px' }}>
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto text-muted" style={{ width: '60px', height: '60px', backgroundColor: '#f1f5f9' }}>
                                        <i className="fa-solid fa-magnifying-glass" style={{ fontSize: '24px' }}></i>
                                    </div>
                                    <h3 className="h5 font-weight-bold text-dark mb-2">No Matching Questions Found</h3>
                                    <p className="text-muted small mb-4">
                                        We couldn't find any questions matching "{searchQuery}". Try searching for another keyword or browse by category.
                                    </p>
                                    <button 
                                        className="btn btn-outline-primary px-4 py-2 mx-auto"
                                        onClick={() => { setSearchQuery(""); setSelectedCategory("all"); }}
                                        style={{ borderRadius: '25px', width: 'max-content' }}
                                    >
                                        Clear Search Filters
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Support & Inquiry Card */}
                    <div className="card border-0 shadow-sm rounded-lg overflow-hidden" style={{ borderRadius: '14px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff' }}>
                        <div className="card-body p-4 p-md-5 text-center">
                            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3" style={{ width: '56px', height: '56px', backgroundColor: 'rgba(1, 210, 142, 0.2)', color: '#01d28e' }}>
                                <i className="fa-solid fa-headset" style={{ fontSize: '26px' }}></i>
                            </div>
                            <h3 className="h4 font-weight-bold text-white mb-2">Still Have Questions?</h3>
                            <p className="text-white-50 mb-4 mx-auto" style={{ maxWidth: '580px', fontSize: '15px' }}>
                                Can't find the answer you're looking for? Reach out to our customer support team directly on WhatsApp or via phone.
                            </p>
                            <div className="d-flex flex-wrap align-items-center justify-content-center" style={{ gap: '15px' }}>
                                <a 
                                    href="https://wa.me/917208388380?text=Hello%20AapliGaadi%2C%20I%20have%20a%20question%20regarding%20car%20rentals." 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="btn btn-success px-4 py-3" 
                                    style={{ borderRadius: '30px', fontWeight: 'bold', backgroundColor: '#25D366', border: 'none', fontSize: '15px' }}
                                >
                                    <i className="fa-brands fa-whatsapp mr-2" style={{ fontSize: '18px' }}></i> Ask on WhatsApp
                                </a>
                                <a 
                                    href="tel:+917208388380" 
                                    className="btn btn-outline-light px-4 py-3" 
                                    style={{ borderRadius: '30px', fontWeight: 'bold', fontSize: '15px' }}
                                >
                                    <i className="fa-solid fa-phone mr-2"></i> Call +91 720 838 8380
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
}

export default FAQs;